import { Instance, types, flow } from "mobx-state-tree";
import axios from "axios";
import {
  LIST_SCHOOLS_API,
  LIST_ACADEMIC_YEARS,
  LIST_LEVEL_SCHOOLS,
  LIST_CLASS_SCHEDULES,
} from "@constants/api";
import { AlertResultApi } from "@utils/alert";
import { fetching, FetchingEnum } from "./Fetching";

export const initialStateSchools = {
  listSchool: [],
  state: "done",
  fetchDetailSchool: "done",
  fetchAcademicYears: FetchingEnum.DONE,
  fetchLevelShools: FetchingEnum.DONE,
};

const SchoolInfo = types.model({
  id: types.number,
  name: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  street: types.maybeNull(types.string),
  unitNumber: types.maybeNull(types.string),
  blockNumber: types.maybeNull(types.string),
  building: types.maybeNull(types.string),
  address: types.maybeNull(types.string),
  postalCode: types.maybeNull(types.string),
  lat: types.number,
  lng: types.number,
  photoUrl: types.maybeNull(types.string),
  status: types.string,
  areaDistanceCalculationType: types.maybeNull(types.string),
  schoolPrefix: types.maybeNull(types.string),
  virtualAccountPrefix: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
});

const academicYear = types.model({
  createdAt: types.string,
  earlyRegistrationEndDate: types.string,
  earlyRegistrationStartDate: types.string,
  endDate: types.string,
  id: types.number,
  lateRegistrationStartDate: types.string,
  name: types.string,
  refundRate: types.number,
  schoolId: types.number,
  startDate: types.string,
  updatedAt: types.maybeNull(types.string),
});

const levelGroups = types.model({
  id: types.number,
  name: types.string,
  status: types.string,
  createdAt: types.string,
  updatedAt: types.maybeNull(types.string),
});

const levelSchool = types.model({
  id: types.number,
  schoolId: types.number,
  nextLevelId: types.maybeNull(types.number),
  name: types.string,
  levelGroups: types.maybeNull(types.array(levelGroups)),
  status: types.string,
  createdAt: types.string,
  updatedAt: types.maybeNull(types.string),
});

const classSchedule = types.model({
  id: types.number,
  levelId: types.number,
  level: types.maybeNull(
    types.model({
      id: types.number,
      schoolId: types.number,
      nextLevelId: types.maybeNull(types.number),
      name: types.string,
      status: types.string,
      createdAt: types.string,
      updatedAt: types.maybeNull(types.string),
    })
  ),
  name: types.maybeNull(types.string),
  status: types.string,
  createdAt: types.string,
  updatedAt: types.maybeNull(types.string),
});

export const Schools = types
  .model({
    listSchools: types.maybeNull(types.array(SchoolInfo)),
    state: types.string,
    status: types.maybeNull(types.number),
    selectSchool: types.maybeNull(SchoolInfo),
    academicYears: types.maybeNull(types.array(academicYear)),
    fetchAcademicYears: fetching,
    statusAcademucYears: types.maybeNull(types.number),
    levelSchools: types.maybeNull(types.array(levelSchool)),
    fetchLevelShools: fetching,
    classSchedules: types.maybeNull(types.array(classSchedule)),
    detailSchool: types.maybeNull(SchoolInfo),
    fetchDetailSchool: types.string,
    statusDetailSchool: types.maybeNull(types.number),
  })
  .actions((self) => ({
    initialSchools: () => {
      self.state = "done";
      self.status = null;
      self.selectSchool = null;
      self.academicYears = null;
      self.detailSchool = null;
      self.levelSchools = null;
      self.fetchAcademicYears = FetchingEnum.DONE;
    },
    getListSchools: flow(function* getListSchools(
      token: string,
      search: string
    ) {
      self.state = "pending";
      self.selectSchool = null;
      self.academicYears = null;
      try {
        // ... yield can be used in async/await style
        const result = yield axios.get(LIST_SCHOOLS_API, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            Search: search,
            PageSize: 1000,
            Filter: JSON.stringify({ Status: "active" }),
          },
        });
        self.state = "done";
        console.log("getListSchools", result.data);
        self.listSchools = result.data;
      } catch (error) {
        // ... including try/catch error handling
        console.log("getListSchoolsError", error);
        self.state = "error";
        self.status = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getAcademicYears: flow(function* getAcademicYears(
      token: string | null,
      SchoolId: number
    ) {
      if (SchoolId === 0) {
        self.fetchAcademicYears = FetchingEnum.DONE;
        self.academicYears = null;
        return;
      }
      self.fetchAcademicYears = "pending";
      self.academicYears = null;
      try {
        // ... yield can be used in async/await style
        const result = yield axios.get(LIST_ACADEMIC_YEARS, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            Filter: JSON.stringify({
              SchoolId: SchoolId,
              StatusIn: ["Ready", "Active"],
            }),
            PageSize: 1000,
          },
        });
        self.fetchAcademicYears = "done";
        console.log("academicYears", result.data);
        self.academicYears = result.data;
      } catch (error) {
        // ... including try/catch error handling
        self.fetchAcademicYears = "error";
        self.academicYears = null;
        self.statusAcademucYears = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
        console.log("academicYearsError", error);
      }
    }),
    getLevelSchools: flow(function* getLevelSchools(
      token: string | null,
      SchoolId: number
    ) {
      if (SchoolId === 0) {
        self.fetchLevelShools = FetchingEnum.DONE;
        self.levelSchools = null;
        return;
      }
      self.fetchLevelShools = "pending";
      self.levelSchools = null;
      try {
        // ... yield can be used in async/await style
        const result = yield axios.get(LIST_LEVEL_SCHOOLS, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            Filter: JSON.stringify({ SchoolId: SchoolId }),
            PageSize: 1000,
          },
        });
        self.fetchLevelShools = "done";
        console.log("getLevelSchools", result.data);
        self.levelSchools = result.data;
      } catch (error) {
        // ... including try/catch error handling
        self.fetchLevelShools = "error";
        self.levelSchools = null;
        self.status = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getClassSchedules: flow(function* getClassSchedules(
      token: string,
      academicYearId: number
    ) {
      self.state = "pending";
      // ... yield can be used in async/await style
      try {
        const result = yield axios.get(LIST_CLASS_SCHEDULES, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            Filter: JSON.stringify({ academicYearId: academicYearId }),
            PageSize: 1000,
          },
        });
        self.state = "done";
        console.log("getClassSchedules", result.data);
        self.classSchedules = result.data;
      } catch (error) {
        console.log("getClassSchedules", error);
        self.state = "error";
        self.status = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getSchoolById: flow(function* getSchoolById(
      token: string,
      schoolId: number
    ) {
      self.fetchDetailSchool = "pending";
      self.detailSchool = null;
      try {
        // ... yield can be used in async/await style
        const result = yield axios.get(`${LIST_SCHOOLS_API}/${schoolId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        self.fetchDetailSchool = "done";
        console.log("getSchoolById", result.data);
        self.detailSchool = result.data;
      } catch (error) {
        console.log("getSchoolById", error);
        self.fetchDetailSchool = "error";
        self.statusDetailSchool = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    setSelectSchool(schoolName: string) {
      if (self.listSchools && schoolName) {
        const index = self.listSchools.findIndex((data) => {
          return data.name?.toLowerCase().includes(schoolName.toLowerCase());
        });
        self.selectSchool =
          index === -1 ? null : { ...self.listSchools[index] };
      }
    },
  }))
  .views((self) => ({
    get listSchoolNames() {
      return self.listSchools
        ? self.listSchools
            .map((data) => (data.name ? data.name : ""))
            .filter((data) => data)
        : [];
    },
    get listAcademicYears() {
      return self.academicYears
        ? self.academicYears.map((data) => ({
            name: data.name,
            id: data.id,
          }))
        : [];
    },
    get listLevelSchools() {
      return self.levelSchools
        ? self.levelSchools.map((data) => ({
            name: data.name,
            id: data.id,
          }))
        : [];
    },
    listClassSchedules(levelId: string) {
      const levelIdNumber = parseInt(levelId, 10);
      if (levelIdNumber && self.levelSchools) {
        const index = self.levelSchools.findIndex((data) => {
          return data.id === levelIdNumber;
        });
        return index === -1 ? [] : self.levelSchools[index].levelGroups;
      }
      return [];
    },
    findLevelId(level: string) {
      if (self.levelSchools) {
        const index = self.levelSchools.findIndex((data) => {
          return data.name === level;
        });
        console.log(index);

        return index === -1 ? null : self.levelSchools[index].id;
      }
    },
    findAcademyYearId(academyYear: string) {
      if (self.academicYears) {
        const index = self.academicYears.findIndex((data) => {
          return data.name === academyYear;
        });
        console.log(index);

        return index === -1 ? null : self.academicYears[index].id;
      }
    },
    findClassId(className: string) {
      if (self.classSchedules) {
        const index = self.classSchedules.findIndex((data) => {
          return data.name === className;
        });
        console.log(index);

        return index === -1 ? null : self.classSchedules[index].id;
      }
    },
  }));

export type RootInstanceSchools = Instance<typeof Schools>;
export type LevelGroupsType = Instance<typeof levelGroups>;
export type SchoolInfoType = Instance<typeof SchoolInfo>;
